import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import consultationService from '../../../services/consultationService';
import { countByQueueFilter, isNursingConsultation } from '../utils/nurseQueueFilters';
import {
  NURSE_QUEUE_POLL_MS,
  nurseQueueFetchDateFrom
} from '../utils/nurseQueueSync';
import { NURSE_QUEUE_REFRESH_EVENT } from '../utils/nurseQueueEvents';

export function useNurseSiteQueue(workSiteId, statsReloadNonce = 0) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consultations, setConsultations] = useState([]);
  const loadQueueRef = useRef(null);
  const workSiteIdRef = useRef(workSiteId);

  workSiteIdRef.current = workSiteId;

  const loadQueue = useCallback(async ({ silent = false } = {}) => {
    if (!workSiteId) {
      setConsultations([]);
      setError('');
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const res = await consultationService.getConsultations({
        site_id: workSiteId,
        date_from: nurseQueueFetchDateFrom()
      });
      if (res.success) {
        setConsultations((res.data || []).filter(isNursingConsultation));
        if (!silent) setError('');
      } else if (!silent) {
        setConsultations([]);
        setError(res.message || 'Chargement de la file impossible');
      }
    } catch (e) {
      if (!silent) {
        setConsultations([]);
        setError(e.message || 'Erreur lors du chargement de la file');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [workSiteId]);

  loadQueueRef.current = loadQueue;

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (!statsReloadNonce) return;
    loadQueue({ silent: true });
  }, [statsReloadNonce, loadQueue]);

  useEffect(() => {
    if (!workSiteId) return undefined;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      loadQueueRef.current?.({ silent: true });
    };

    const intervalId = setInterval(tick, NURSE_QUEUE_POLL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadQueueRef.current?.({ silent: true });
      }
    };

    const onQueueRefresh = (event) => {
      const detailSiteId = event?.detail?.siteId;
      if (
        detailSiteId != null &&
        String(detailSiteId) !== String(workSiteIdRef.current)
      ) {
        return;
      }
      loadQueueRef.current?.({ silent: true });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(NURSE_QUEUE_REFRESH_EVENT, onQueueRefresh);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(NURSE_QUEUE_REFRESH_EVENT, onQueueRefresh);
    };
  }, [workSiteId]);

  const counts = useMemo(() => countByQueueFilter(consultations), [consultations]);

  const upsertConsultation = useCallback((consultation) => {
    if (!consultation?.id || !isNursingConsultation(consultation)) {
      return false;
    }
    const id = Number(consultation.id);
    setConsultations((prev) => {
      const rest = prev.filter((row) => Number(row.id) !== id);
      return [consultation, ...rest];
    });
    setError('');
    return true;
  }, []);

  const syncAfterPatientReceived = useCallback(
    async (consultation) => {
      if (consultation?.id) {
        upsertConsultation(consultation);
      }
      await loadQueue({ silent: true });
    },
    [upsertConsultation, loadQueue]
  );

  return useMemo(
    () => ({
      consultations,
      loading,
      error,
      loadQueue,
      upsertConsultation,
      syncAfterPatientReceived,
      counts
    }),
    [
      consultations,
      loading,
      error,
      loadQueue,
      upsertConsultation,
      syncAfterPatientReceived,
      counts
    ]
  );
};
